import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, BehaviorSubject, EMPTY, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { SharedService } from './shared.service';
import { ToastrService } from 'ngx-toastr';
import { stringConstant } from './stringConstants';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './authentication.service';
import { UuidService } from './uuid.service';
import { RefreshTokenRequest } from '../models/shared.model';

// Global state for token refresh
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sharedService = inject(SharedService);
  const authenticationService = inject(AuthenticationService);
  const toastrService = inject(ToastrService);
  const uuidService = inject(UuidService);

  const token = sharedService.getLocalStorage(stringConstant.token);
  const traceId = uuidService.generateUuid();

  let modifiedReq = req;

  // Skip adding Authorization header for app token requests
  if (req.url.includes(stringConstant.appTokenApiUrl)) {
    modifiedReq = req.clone({
      setHeaders: {
        'Trace-Id': traceId,
      },
    });
  } else if (token) {
    // Only add Authorization header if token exists
    modifiedReq = req.clone({
      setHeaders: {
        Authorization: `${stringConstant.bearer} ${token}`,
        'Trace-Id': traceId,
      },
    });
  } else {
    // No token, just add trace ID
    modifiedReq = req.clone({
      setHeaders: {
        'Trace-Id': traceId,
      },
    });
  }

  return next(modifiedReq).pipe(
    catchError((err) => {
      // Only try to refresh token if user is logged in (has token)
      if (err.status === 401 && !isRefreshing && token) {
        // Token might be expired, start refreshing it
        isRefreshing = true;
        refreshTokenSubject.next(null);

        const tokenData = JSON.parse(
          sharedService.getLocalStorage(
            `${stringConstant.updateAccessToken}::${environment.auth.clientId}::${environment.auth.authorizationParams.audience}::${environment.auth.authorizationParams.scope}`
          )
        );

        let credentials: RefreshTokenRequest = {
          accessToken: '',
          refreshToken: '',
        };

        if (credentials) {
          credentials = {
            accessToken: sharedService.getLocalStorage(stringConstant.token),
            refreshToken: tokenData.body.refresh_token,
          };
        }

        return authenticationService.getUpdatedAccessToken(credentials).pipe(
          switchMap((res) => {
            const newToken = res.accessToken;
            tokenData.body.access_token = newToken;
            tokenData.body.refresh_token = res.refreshToken;

            sharedService.setLocalStorage(
              `${stringConstant.updateAccessToken}::${environment.auth.clientId}::${environment.auth.authorizationParams.audience}::${environment.auth.authorizationParams.scope}`,
              JSON.stringify(tokenData)
            );

            sharedService.setLocalStorage(stringConstant.token, newToken);
            isRefreshing = false;
            refreshTokenSubject.next(newToken);

            // Retry all queued requests
            return refreshTokenSubject.pipe(
              filter((token) => token != null),
              take(1),
              switchMap(() => {
                return next(
                  modifiedReq.clone({
                    setHeaders: {
                      Authorization: `${stringConstant.bearer} ${newToken}`,
                      'Trace-Id': traceId,
                    },
                  })
                );
              })
            );
          }),
          catchError(() => {
            toastrService.error(stringConstant.sessionExpireError, '', {
              timeOut: 3000,
            });
            authenticationService.auth0Logout();
            isRefreshing = false;
            return EMPTY;
          })
        );
      } else if (err.status === 401 && isRefreshing) {
        // If refresh is in progress, wait until it's done
        return refreshTokenSubject.pipe(
          filter((token) => token != null),
          take(1),
          switchMap((token) => {
            return next(
              modifiedReq.clone({
                setHeaders: {
                  Authorization: `${stringConstant.bearer} ${token}`,
                  'Trace-Id': traceId,
                },
              })
            );
          })
        );
      } else if (err.status === 401 && !token) {
        // For non-logged-in users, just pass through the error
        // The app token service will handle retry logic
        return throwError(() => err);
      } else {
        return throwError(() => err);
      }
    })
  );
};